import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Plot from 'react-plotly.js';

const LeakGrowth = ({ data, alertItem }, context) => {
	const [layout, setLayout] = useState({
		height: 350,
		autosize: true,
		showlegend: true,
		legend: {
			x: 1,
			xanchor: 'right',
			y: 1,
			bgcolor: 'rgba(255,255,255,0.8)',
			bordercolor: '#e0e0e0',
			borderwidth: 1,
		},
		margin: {
			t: 20,
			b: 60,
			l: 60,
			r: 20,
			pad: 4
		},
		xaxis: {
			title: {
				text: context.t('date'),
				standoff: 10
			},
			tickformat: '%b %d',
			gridcolor: '#f0f0f0',
			linecolor: '#e0e0e0',
		},
		yaxis: {
			title: {
				text: context.t('intensity'),
				standoff: 10
			},
			gridcolor: '#f0f0f0',
			linecolor: '#e0e0e0',
			rangemode: 'tozero'
		},
		plot_bgcolor: '#fafafa',
		paper_bgcolor: 'white',
	});

	const [config] = useState({
		displaylogo: false,
		responsive: true,
		displayModeBar: true,
		modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d'],
		modeBarPosition: 'top',
	});

	const [plotData, setPlotData] = useState([]);

	useEffect(() => {
		generatePlotData(data);
	}, [data]);

	const generatePlotData = (data) => {
		if (!data || !Array.isArray(data.y) || data.y.length === 0) {
			return;
		}
		const newPlotData = [{
			x: data.x,
			y: data.y,
			mode: 'lines+markers',
			type: 'scatter',
			name: context.t('intensities'),
			line: {
				color: '#1976d2',
				width: 2
			},
			marker: {
				color: '#1976d2',
				size: 6
			}
		}, {
			x: data.x,
			y: data.trendData.points,
			mode: 'lines',
			type: 'scatter',
			name: context.t('trend'),
			line: {
				color: '#ff7043',
				width: 3,
				dash: 'solid'
			}
		}];

		const maxValue = Math.max(...data.y);

		setPlotData(newPlotData);
		setLayout((current) => ({
			...current,
			yaxis: {
				...current.yaxis,
				range: [0, Math.max(100, maxValue * 1.15)]
			}
		}));
	};

	const renderNoData = () => {
		return (
			<div style={{
				padding: '40px',
				textAlign: 'center',
				color: '#666',
				minWidth: '400px'
			}}>
				{context.t('no_data')}
			</div>
		);
	};

	if (data == null || data.x?.length === 0) {
		return renderNoData();
	}

	const slopeValue = alertItem?.AvgGrowthAngle ||
		(data.trendData ? (Math.atan(data.trendData.slope) * (180 / Math.PI)).toFixed(2) : null);

	return (
		<div style={{ padding: '10px' }}>
			{data.trendData && slopeValue && (
				<div style={{
					marginBottom: '15px',
					padding: '10px 16px',
					backgroundColor: '#f5f5f5',
					borderRadius: '6px',
					display: 'inline-block',
					fontSize: '15px',
					color: '#333'
				}}>
					{context.t('slope', { slope: slopeValue + '°' })}
				</div>
			)}

			<Plot
				data={plotData}
				layout={layout}
				config={config}
				style={{ width: '100%' }}
			/>
		</div>
	);
}

LeakGrowth.contextTypes = {
	t: PropTypes.func.isRequired
}

export default LeakGrowth;
